'use client';

import { getClientEntitlement } from '@/api/entitlement';
import { checkEntitlementGate } from '@/app/lib/entitlement';
import type {
  ClientEntitlement,
  EntitlementGateDescriptor,
  EntitlementGateResult,
  EntitlementGateVariant,
} from '@/types/entitlement';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useRef, useState } from 'react';

type GuardedAction<T> = () => Promise<T> | T;

type ModalState = {
  isOpen: boolean;
  variant: EntitlementGateVariant | null;
  gateResult: EntitlementGateResult | null;
};

const defaultModalState: ModalState = {
  isOpen: false,
  variant: null,
  gateResult: null,
};

export function useClientEntitlement(clientIdOverride?: string) {
  const clientId = clientIdOverride;

  return useQuery({
    queryKey: ['client-entitlement', clientId],
    queryFn: () => getClientEntitlement(clientId || ''),
    enabled: !!clientId,
  });
}

export function useEntitlementGate(clientIdOverride?: string) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const clientId = clientIdOverride;
  const [modalState, setModalState] = useState<ModalState>(defaultModalState);
  const [isCheckingGate, setIsCheckingGate] = useState(false);
  const [checkingDescriptor, setCheckingDescriptor] = useState<EntitlementGateDescriptor | null>(
    null
  );
  const isGuardRunningRef = useRef(false);

  const closeModal = useCallback(() => {
    setModalState(defaultModalState);
  }, []);

  const openPlans = useCallback(() => {
    if (!clientId) {
      return;
    }

    closeModal();
    router.push(`/${clientId}/billing`);
  }, [clientId, closeModal, router]);

  const openGateModal = useCallback((gateResult: EntitlementGateResult) => {
    if (gateResult.allowed) {
      return;
    }

    setModalState({
      isOpen: true,
      variant: gateResult.variant,
      gateResult,
    });
  }, []);

  const getLatestEntitlement = useCallback(async (): Promise<ClientEntitlement | undefined> => {
    if (!clientId) {
      return undefined;
    }

    const freshEntitlement = await getClientEntitlement(clientId);
    queryClient.setQueryData(['client-entitlement', clientId], freshEntitlement);
    return freshEntitlement;
  }, [clientId, queryClient]);

  const checkGate = useCallback(
    async (descriptor: EntitlementGateDescriptor) => {
      const entitlement = await getLatestEntitlement();

      return checkEntitlementGate(entitlement, descriptor);
    },
    [getLatestEntitlement]
  );

  const guard = useCallback(
    async <T,>(descriptor: EntitlementGateDescriptor, action: GuardedAction<T>) => {
      if (isGuardRunningRef.current) {
        return null;
      }

      isGuardRunningRef.current = true;
      setIsCheckingGate(true);
      setCheckingDescriptor(descriptor);

      let gateResult: EntitlementGateResult;
      try {
        gateResult = await checkGate(descriptor);
      } finally {
        isGuardRunningRef.current = false;
        setIsCheckingGate(false);
        setCheckingDescriptor(null);
      }

      if (!gateResult.allowed) {
        openGateModal(gateResult);
        return null;
      }

      return await action();
    },
    [checkGate, openGateModal]
  );

  const isCheckingGateFor = useCallback(
    (descriptor: EntitlementGateDescriptor) =>
      isCheckingGate &&
      checkingDescriptor?.resource === descriptor.resource &&
      checkingDescriptor.action === descriptor.action &&
      checkingDescriptor.permissionGroup === descriptor.permissionGroup,
    [checkingDescriptor, isCheckingGate]
  );

  return useMemo(
    () => ({
      clientId,
      checkGate,
      guard,
      isCheckingGate,
      isCheckingGateFor,
      modalState,
      closeModal,
      openGateModal,
      openPlans,
    }),
    [
      checkGate,
      clientId,
      closeModal,
      guard,
      isCheckingGate,
      isCheckingGateFor,
      modalState,
      openGateModal,
      openPlans,
    ]
  );
}
